// Client for the Go Advisor API (END-149, docs/advisor/conversations.md).
// The browser talks only to Go: it submits a question, reads the proxied v1
// SSE stream, and looks messages up by client_request_id after a disconnect.
// Python is never reachable from here.

// ── Types (mirror contracts/advisor/v6) ──────────────────────────────────────

export type EvidenceKind = 'finding' | 'function_source' | 'findings_search' | 'function_search' | 'source_chunk';

/**
 * Evidence as Go issued it. Live `evidence` events and stored citations share
 * this shape. Repository, scan and commit are those of the scan the evidence
 * was read from; all null only for a findings search over several
 * repositories of an "All repositories" chat (contract v3).
 */
export interface AdvisorEvidence {
  evidence_id: string;
  kind: EvidenceKind;
  repository_id: string | null;
  analysis_run_id: string | null;
  commit_sha: string | null;
  path: string | null;
  start_line: number | null;
  end_line: number | null;
  function: string | null;
  fingerprint: string | null;
}

export interface Claim {
  text: string;
  evidence_ids: string[];
}

export interface SuggestedRefactor {
  target_evidence_id: string;
  reason: string;
  proposed_split: { name: string; responsibility: string; source_lines: string | null }[];
  effort: { low: number; high: number; unit: 'hours' };
  assumptions: string[];
  behavior_preservation_tests: string[];
  risks: string[];
  evidence_ids: string[];
}

export interface FinalAnswer {
  status: 'answered' | 'insufficient_evidence';
  summary: string;
  claims?: Claim[];
  suggested_refactor?: SuggestedRefactor | null;
  limitations?: string[];
}

export type MessageState = 'streaming' | 'completed' | 'failed' | 'incomplete';

/** One repository scan of a chat's fixed scope (END-172). */
export interface ScopeRepository {
  repository_id: string;
  analysis_run_id: string;
  commit_sha: string | null;
  name: string;
  platform?: string;
}

/** What a chat is about: one repository's scan, or several repositories' scans. */
export interface ConversationScope {
  kind: 'repository' | 'organization';
  repositories: ScopeRepository[];
}

export interface AdvisorMessage {
  id: string;
  conversation_id: string;
  /** The scan of a repository chat; null in an "All repositories" chat. */
  repository_id: string | null;
  analysis_run_id: string | null;
  commit_sha: string | null;
  scope?: ConversationScope;
  client_request_id: string;
  question: string;
  state: MessageState;
  failure_code: string | null;
  answer: FinalAnswer | null;
  citations: AdvisorEvidence[];
  /**
   * Earlier answers of the chat sent with this question, and how many the
   * history limit left out (END-171). Both null for messages from before
   * follow-ups had context: those were answered on their own.
   */
  history_turns: number | null;
  history_omitted: number | null;
  created_at: string;
  finished_at: string | null;
}

export interface AdvisorConversation {
  id: string;
  repository_id: string | null;
  analysis_run_id: string | null;
  scope?: ConversationScope;
  created_at: string;
  updated_at: string;
}

/** A conversation as listed: enough to show and reopen it, without its messages. */
export interface AdvisorConversationSummary {
  id: string;
  repository_id: string | null;
  analysis_run_id: string | null;
  commit_sha: string | null;
  scope?: ConversationScope;
  /** The first question, at most 120 characters. */
  title: string;
  created_at: string;
  updated_at: string;
}

export interface AdvisorConversationPage {
  conversations: AdvisorConversationSummary[];
  next_cursor: string | null;
}

export type AdvisorEvent =
  | { type: 'request.started'; data: { request_id: string; provider: string; model: string | null } }
  | { type: 'answer.delta'; data: { request_id: string; index: number; text: string } }
  | { type: 'evidence'; data: { request_id: string; evidence: AdvisorEvidence } }
  | { type: 'answer.completed'; data: { request_id: string; text_chars: number; deltas: number; answer: FinalAnswer } }
  | { type: 'request.failed'; data: { request_id: string; code: string; message: string } };

const EVENT_NAMES = new Set(['request.started', 'answer.delta', 'evidence', 'answer.completed', 'request.failed']);
// Contract versions whose events this client reads. v2 (END-171) changed only
// what Go sends Python, v3 (END-172) only lets one evidence record belong
// to no single scan, v4 (END-174) only lets a function be a Go method
// such as (*Engine).processJob, v5 (END-177) only adds scan coverage to
// what Go sends Python, and v6 (END-152) only adds source_chunk evidence (a
// part of one function's source, found by code search); the events are
// otherwise the same, so a page loaded before or after a rollout reads any
// of them.
const EVENT_VERSIONS = new Set([1, 2, 3, 4, 5, 6]);

/** A refusal before streaming: `{error, message}` with its HTTP status. */
export class AdvisorRefusal extends Error {
  constructor(public status: number, public code: string, message: string) {
    super(message);
    this.name = 'AdvisorRefusal';
  }
}

// ── SSE parsing ──────────────────────────────────────────────────────────────

export interface SSEFrame {
  event: string;
  data: string;
  id?: string;
}

/**
 * Incremental text/event-stream parser (HTML spec): LF, CRLF or CR line
 * endings, comments (heartbeats) skipped, one leading space after the colon
 * dropped, multi-line data joined with "\n", events without data not
 * dispatched. Feed it decoded text in any chunking; call it with the decoder
 * in streaming mode so split UTF-8 characters are held back.
 */
export class SSEParser {
  private buffer = '';
  private event = '';
  private data: string[] = [];
  private id: string | undefined;
  private started = false;

  push(text: string): SSEFrame[] {
    this.buffer += text;
    if (!this.started && this.buffer.length > 0) {
      this.started = true;
      if (this.buffer.charCodeAt(0) === 0xfeff) this.buffer = this.buffer.slice(1);
    }
    const frames: SSEFrame[] = [];
    for (;;) {
      const lf = this.buffer.indexOf('\n');
      const cr = this.buffer.indexOf('\r');
      let end: number;
      let next: number;
      if (cr >= 0 && (lf < 0 || cr < lf)) {
        // A CR at the end of the buffer may be the first half of a CRLF.
        if (cr === this.buffer.length - 1) break;
        end = cr;
        next = this.buffer[cr + 1] === '\n' ? cr + 2 : cr + 1;
      } else if (lf >= 0) {
        end = lf;
        next = lf + 1;
      } else {
        break;
      }
      const line = this.buffer.slice(0, end);
      this.buffer = this.buffer.slice(next);
      const frame = this.line(line);
      if (frame) frames.push(frame);
    }
    return frames;
  }

  private line(line: string): SSEFrame | null {
    if (line === '') {
      const frame = this.data.length > 0 ? { event: this.event || 'message', data: this.data.join('\n'), id: this.id } : null;
      this.event = '';
      this.data = [];
      return frame;
    }
    if (line.startsWith(':')) return null;
    const colon = line.indexOf(':');
    const field = colon < 0 ? line : line.slice(0, colon);
    let value = colon < 0 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);
    if (field === 'data') this.data.push(value);
    else if (field === 'event') this.event = value;
    else if (field === 'id' && !value.includes('\0')) this.id = value;
    return null;
  }
}

/** Parses a frame into a known v1 event; anything else is ignored (null). */
export function toAdvisorEvent(frame: SSEFrame): AdvisorEvent | null {
  if (!EVENT_NAMES.has(frame.event)) return null;
  try {
    const data = JSON.parse(frame.data);
    if (!data || typeof data !== 'object' || !EVENT_VERSIONS.has(data.v)) return null;
    return { type: frame.event, data } as AdvisorEvent;
  } catch {
    return null;
  }
}

// ── HTTP ─────────────────────────────────────────────────────────────────────

const API_URL = import.meta.env.VITE_API_BASE_URL ?? '';

type FetchLike = typeof fetch;

function headers(json = true): Record<string, string> {
  const h: Record<string, string> = {};
  if (json) h['Content-Type'] = 'application/json';
  const token = localStorage.getItem('auth_token');
  if (token) h['Authorization'] = `Bearer ${token}`;
  // Go ignores organization selectors for Advisor routes (the repository
  // decides), but the rest of the API expects the header.
  const org = localStorage.getItem('organization_id');
  if (org) h['X-Organization-ID'] = org;
  return h;
}

async function refusal(response: Response): Promise<AdvisorRefusal> {
  let code = 'internal_error';
  let message = `The Advisor request failed (HTTP ${response.status}).`;
  try {
    const body = await response.json();
    if (typeof body?.error === 'string') code = body.error;
    if (typeof body?.message === 'string') message = body.message;
  } catch {
    /* not JSON */
  }
  return new AdvisorRefusal(response.status, code, message);
}

/**
 * What an Advisor call is about: a repository (its id), or the "All
 * repositories" chats of an organization. The organization selects; Go checks
 * membership and every scoped repository on each call.
 */
export type AdvisorTarget = string | { organizationId: string };

/** The "All repositories" workspace's path segment, in place of a repository id. */
export const ALL_REPOSITORIES = 'all';

function advisorBase(target: AdvisorTarget): string {
  if (typeof target === 'string') return `${API_URL}/api/repositories/${encodeURIComponent(target)}/advisor`;
  return `${API_URL}/api/organizations/${encodeURIComponent(target.organizationId)}/advisor`;
}

export interface SubmitRequest {
  /** A repository chat's repository; or organizationId for an "All repositories" chat. */
  repositoryId?: string;
  organizationId?: string;
  /**
   * Exactly one of: conversationId; analysisRunId (a new repository chat);
   * scope (a new "All repositories" chat: the confirmed scans, in order).
   */
  conversationId?: string;
  analysisRunId?: string;
  scope?: { repository_id: string; analysis_run_id: string }[];
  message: string;
  clientRequestId: string;
}

export interface StreamStart {
  conversationId: string | null;
  messageId: string | null;
  /** The conversation context Go sent with the question; null if the headers are missing. */
  history: HistoryScope | null;
}

/** How much of the chat an answer saw as context. */
export interface HistoryScope {
  turns: number;
  omitted: number;
}

function headerCount(value: string | null): number | null {
  if (value === null || !/^\d{1,6}$/.test(value)) return null;
  return Number(value);
}

/** The scope a stored message recorded; null for messages answered on their own. */
export function messageHistory(message: AdvisorMessage): HistoryScope | null {
  if (message.history_turns == null || message.history_omitted == null) return null;
  return { turns: message.history_turns, omitted: message.history_omitted };
}

export type SubmitResult =
  /** A new generation: events were delivered through onEvent. `terminal` is false when the stream ended without a terminal event. */
  | { kind: 'stream'; start: StreamStart; terminal: boolean }
  /** A repeated client_request_id: the stored message, nothing was generated. */
  | { kind: 'replay'; message: AdvisorMessage };

/**
 * Submits one question. Resolves once the stream ends (or on replay); throws
 * AdvisorRefusal for a refusal before streaming, and the AbortError when
 * `signal` stops it. Go records a stopped generation as incomplete and never
 * restarts it.
 */
export async function submitQuestion(
  req: SubmitRequest,
  handlers: { onStart?: (start: StreamStart) => void; onEvent: (event: AdvisorEvent) => void },
  signal?: AbortSignal,
  fetchImpl: FetchLike = fetch,
): Promise<SubmitResult> {
  const body: Record<string, unknown> = { message: req.message, client_request_id: req.clientRequestId };
  if (req.conversationId) body.conversation_id = req.conversationId;
  else if (req.analysisRunId) body.analysis_run_id = req.analysisRunId;
  else if (req.scope) body.scope = req.scope;
  const target: AdvisorTarget = req.organizationId ? { organizationId: req.organizationId } : (req.repositoryId ?? '');
  const response = await fetchImpl(`${advisorBase(target)}/messages`, {
    method: 'POST',
    headers: { ...headers(), Accept: 'text/event-stream' },
    body: JSON.stringify(body),
    signal,
  });
  if (!response.ok) throw await refusal(response);

  const type = response.headers.get('Content-Type') ?? '';
  if (response.headers.get('X-Advisor-Replay') === 'true' || type.includes('application/json')) {
    const json = await response.json();
    return { kind: 'replay', message: json.message as AdvisorMessage };
  }

  const turns = headerCount(response.headers.get('X-Advisor-History-Turns'));
  const omitted = headerCount(response.headers.get('X-Advisor-History-Omitted'));
  const start: StreamStart = {
    conversationId: response.headers.get('X-Advisor-Conversation-Id'),
    messageId: response.headers.get('X-Advisor-Message-Id'),
    history: turns !== null && omitted !== null ? { turns, omitted } : null,
  };
  handlers.onStart?.(start);
  if (!response.body) return { kind: 'stream', start, terminal: false };

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  const parser = new SSEParser();
  let terminal = false;
  try {
    for (;;) {
      const { done, value } = await reader.read();
      const text = done ? decoder.decode() : decoder.decode(value, { stream: true });
      for (const frame of parser.push(text)) {
        const event = toAdvisorEvent(frame);
        if (!event || terminal) continue;
        handlers.onEvent(event);
        if (event.type === 'answer.completed' || event.type === 'request.failed') terminal = true;
      }
      if (done || terminal) break;
    }
  } finally {
    if (terminal) reader.cancel().catch(() => undefined);
  }
  return { kind: 'stream', start, terminal };
}

export async function getRequestStatus(
  target: AdvisorTarget,
  clientRequestId: string,
  signal?: AbortSignal,
  fetchImpl: FetchLike = fetch,
): Promise<AdvisorMessage | null> {
  const response = await fetchImpl(`${advisorBase(target)}/requests/${encodeURIComponent(clientRequestId)}`, {
    headers: headers(false),
    signal,
  });
  if (response.status === 404) return null;
  if (!response.ok) throw await refusal(response);
  return (await response.json()).message as AdvisorMessage;
}

export interface AdvisorAvailability {
  available: boolean;
  /** not_enabled: the pilot does not include this organization; unavailable: the service is down or restarting. */
  reason?: 'not_enabled' | 'unavailable';
}

export async function getAvailability(
  target: AdvisorTarget,
  signal?: AbortSignal,
  fetchImpl: FetchLike = fetch,
): Promise<AdvisorAvailability> {
  const response = await fetchImpl(`${advisorBase(target)}/availability`, {
    headers: headers(false),
    signal,
  });
  if (!response.ok) throw await refusal(response);
  return response.json();
}

export async function getConversation(
  target: AdvisorTarget,
  conversationId: string,
  signal?: AbortSignal,
  fetchImpl: FetchLike = fetch,
): Promise<{ conversation: AdvisorConversation; messages: AdvisorMessage[] } | null> {
  const response = await fetchImpl(`${advisorBase(target)}/conversations/${encodeURIComponent(conversationId)}`, {
    headers: headers(false),
    signal,
  });
  if (response.status === 404 || response.status === 403) return null;
  if (!response.ok) throw await refusal(response);
  return response.json();
}

/** One page of the user's conversations about a repository (or all of an organization's), most recently updated first. */
export async function listConversations(
  target: AdvisorTarget,
  { cursor, limit }: { cursor?: string | null; limit?: number } = {},
  signal?: AbortSignal,
  fetchImpl: FetchLike = fetch,
): Promise<AdvisorConversationPage> {
  const params = new URLSearchParams();
  if (cursor) params.set('cursor', cursor);
  if (limit) params.set('limit', String(limit));
  const query = params.toString();
  const response = await fetchImpl(`${advisorBase(target)}/conversations${query ? `?${query}` : ''}`, {
    headers: headers(false),
    signal,
  });
  if (!response.ok) throw await refusal(response);
  return response.json();
}

/** A repository a new "All repositories" chat can include, with its latest completed scan. */
export interface ScopeCandidate {
  repository_id: string;
  name: string;
  platform?: string;
  analysis_run_id: string;
  commit_sha: string | null;
  completed_at: string | null;
}

/** What a new "All repositories" chat could include, to confirm before asking. */
export interface ScopePreview {
  repositories: ScopeCandidate[];
  /** This organization's repositories that cannot be included (no completed scan yet). */
  excluded: { repository_id: string; name: string; reason: 'no_completed_scan' }[];
  /** More repositories exist than one preview lists. */
  truncated: boolean;
  /** The most repositories one chat can include. */
  max_repositories: number;
}

/** The repositories a new "All repositories" chat in this organization could include. */
export async function getScopePreview(organizationId: string, signal?: AbortSignal, fetchImpl: FetchLike = fetch): Promise<ScopePreview> {
  const response = await fetchImpl(`${advisorBase({ organizationId })}/scope`, { headers: headers(false), signal });
  if (!response.ok) throw await refusal(response);
  return response.json();
}

// ── Presentation helpers ────────────────────────────────────────────────────

/** Questions that start an "All repositories" chat. */
export const ORGANIZATION_STARTER_QUESTIONS = [
  'Which repository has the most critical findings, and where should we start?',
  'Which repositories have vulnerable dependencies with a fixed version available?',
  'What are the riskiest functions across these repositories?',
];

/** Questions that start a chat; follow-ups can refer back to their answers. */
export const STARTER_QUESTIONS = [
  'Which findings in this scan should we fix first, and why?',
  'Which function is the best refactoring candidate, and how would you split it?',
  'Are there vulnerable dependencies with a fixed version available?',
  'Which files concentrate the most technical debt?',
];

/** What a refusal or failure code means for the person asking. */
export function describeRefusal(code: string): { title: string; detail: string; retryable: boolean } {
  switch (code) {
    case 'advisor_busy':
      return { title: 'Another Advisor request is running', detail: 'Your organization runs one Advisor request at a time. Try again when it finishes.', retryable: true };
    case 'advisor_budget_exhausted':
      return { title: 'Monthly Advisor budget used up', detail: "Your organization's Advisor spend limit for this month has been reached. It resets at the start of next month.", retryable: false };
    case 'advisor_not_entitled':
      return { title: 'Advisor is not included in your plan', detail: 'Upgrade to Pro to ask the Advisor about your scans.', retryable: false };
    case 'forbidden':
      return { title: 'No access to this scan', detail: 'You no longer have access to this repository or scan. Ask an organization admin, or switch to the organization that owns it.', retryable: false };
    case 'conversation_not_found':
      return { title: 'Conversation not available', detail: 'This conversation is no longer accessible. Start a new one.', retryable: false };
    case 'advisor_unavailable':
      return { title: 'Advisor unavailable', detail: 'The Advisor is not available right now.', retryable: true };
    case 'advisor_not_available':
      return { title: 'Advisor not enabled', detail: 'The Advisor is in a limited pilot and is not enabled for your organization.', retryable: false };
    case 'client_request_id_reused':
      return { title: 'Duplicate submission', detail: 'This submission was already used for a different question. Ask again.', retryable: true };
    case 'invalid_request':
      return { title: 'Question not accepted', detail: 'Check the question and try again.', retryable: false };
    default:
      return { title: 'The Advisor request failed', detail: 'Something went wrong. You can ask again.', retryable: true };
  }
}

/** Fixed text for stored failure codes (message.failure_code). */
export function describeFailure(code: string | null): string {
  switch (code) {
    case 'client_disconnected':
      return 'Stopped before an answer was ready. Nothing was saved from this attempt.';
    case 'deadline_exceeded':
      return 'The Advisor ran out of time before answering.';
    case 'upstream_interrupted':
    case 'protocol_error':
    case 'abandoned':
      return 'The answer stream was interrupted before an answer was ready.';
    case 'service_restarted':
      return 'The service restarted while the Advisor was answering. Nothing was saved; you can ask again.';
    case 'access_revoked':
      return 'Your access to a repository or scan of this chat changed while the Advisor was answering. Nothing was saved.';
    case 'persistence_failed':
      return 'The answer could not be saved, so it is not shown.';
    case 'advisor_unavailable':
      return 'The Advisor was unavailable.';
    case 'invalid_citation':
      return 'The answer cited evidence it was not given, so it was rejected.';
    case 'provider_unavailable':
      return 'The model provider is unavailable. Try again later.';
    case 'budget_exceeded':
      return 'The request reached its spend limit before an answer was ready.';
    case 'budget_unavailable':
      return 'Advisor spend limits could not be checked.';
    case 'loop_limit_reached':
      return 'The question needed more context than one request allows. Try a narrower question.';
    case 'no_valid_answer':
      return 'The Advisor could not produce an answer backed by scan evidence.';
    case 'evidence_unavailable':
      return 'Scan evidence could not be read.';
    default:
      return 'The Advisor request failed.';
  }
}

/** A link to a scanned commit on GitHub, or null when the platform or commit is unknown. */
export function commitLink(repo: { platform?: string; full_name?: string }, sha: string | null): string | null {
  if (!sha || !repo.full_name || (repo.platform ?? '').toLowerCase() !== 'github') return null;
  return `https://github.com/${repo.full_name}/commit/${sha}`;
}

export function shortSha(sha: string | null | undefined): string {
  return sha ? sha.slice(0, 7) : '';
}

/**
 * A link to the cited lines at the scanned commit, or null when the scan has
 * no commit, the platform is not GitHub, or the evidence names no file.
 * Never links to a branch: the answer is about the pinned commit.
 */
export function sourceLink(
  evidence: AdvisorEvidence,
  repo: { platform?: string; full_name?: string } | null | undefined,
): string | null {
  if (!repo?.full_name || (repo.platform ?? '').toLowerCase() !== 'github') return null;
  if (!evidence.commit_sha || !/^[0-9a-f]{40}([0-9a-f]{24})?$/.test(evidence.commit_sha) || !evidence.path) return null;
  const path = evidence.path.split('/').map(encodeURIComponent).join('/');
  const repoPath = repo.full_name.split('/').map(encodeURIComponent).join('/');
  let anchor = '';
  if (evidence.start_line) {
    anchor = `#L${evidence.start_line}`;
    if (evidence.end_line && evidence.end_line !== evidence.start_line) anchor += `-L${evidence.end_line}`;
  }
  return `https://github.com/${repoPath}/blob/${evidence.commit_sha}/${path}${anchor}`;
}

export function describeEvidence(evidence: AdvisorEvidence): string {
  const lines = evidence.start_line
    ? `:${evidence.start_line}${evidence.end_line && evidence.end_line !== evidence.start_line ? `-${evidence.end_line}` : ''}`
    : '';
  switch (evidence.kind) {
    case 'findings_search':
      return evidence.repository_id ? 'Search of the scan findings' : "Search of every repository's scan findings";
    case 'function_search':
      return `Search for ${evidence.function ?? 'a function'} in ${evidence.path ?? 'the scan'}`;
    case 'function_source':
    case 'source_chunk':
      return `${evidence.function ? `${evidence.function} · ` : ''}${evidence.path ?? ''}${lines}`;
    default:
      return `${evidence.path ?? ''}${lines}${evidence.function ? ` · ${evidence.function}` : ''}`;
  }
}

/** What the Advisor did to obtain a piece of evidence, as one line of tool activity. */
export function describeActivity(evidence: AdvisorEvidence): string {
  switch (evidence.kind) {
    case 'findings_search':
      return evidence.repository_id ? 'Searched the scan findings' : "Searched every repository's scan findings";
    case 'function_search':
      return `Looked up ${evidence.function ?? 'a function'}${evidence.path ? ` in ${evidence.path}` : ''}`;
    case 'function_source':
      return `Read the source of ${describeEvidence(evidence)}`;
    case 'source_chunk':
      return `Found by code search: ${describeEvidence(evidence)}`;
    default:
      return `Read the finding at ${describeEvidence(evidence)}`;
  }
}

/** The Advisor workspace URL of a project, or of one of its chats. */
export function advisorPath(repositoryId?: string | null, conversationId?: string | null): string {
  if (!repositoryId) return '/advisor';
  const project = `/advisor/${encodeURIComponent(repositoryId)}`;
  return conversationId ? `${project}/${encodeURIComponent(conversationId)}` : project;
}

// ── Session changes in other tabs ───────────────────────────────────────────

// Set once another tab signs out, signs in (as anyone) or clears storage. This
// module is loaded with AuthContext, so the listener lives as long as the
// application, including while no Advisor page is open; the flag holds until
// the page reloads, which revalidates the session.
let sessionChanged = false;
const sessionListeners = new Set<() => void>();

/** Records that this page's session no longer matches the stored credentials. */
export function markAdvisorSessionChanged(): void {
  if (sessionChanged) return;
  sessionChanged = true;
  sessionListeners.forEach((listener) => listener());
}

/** Whether another tab changed the credentials since this page loaded. */
export function advisorSessionChanged(): boolean {
  return sessionChanged;
}

/** Calls listener when the session changes; returns the unsubscribe function. */
export function onAdvisorSessionChanged(listener: () => void): () => void {
  sessionListeners.add(listener);
  return () => {
    sessionListeners.delete(listener);
  };
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === null || e.key === 'auth_token') markAdvisorSessionChanged();
  });
}

// ── Per-organization storage ────────────────────────────────────────────────

const STORAGE_PREFIX = 'debtdrone.advisor.';

/** The project and chat last open in the Advisor workspace. */
export interface AdvisorSelection {
  repositoryId: string;
  conversationId: string | null;
}

/** Where the last selection is remembered, per user and organization. */
export function selectionStorageKey(userId: string, orgId: string): string {
  return `${STORAGE_PREFIX}${userId}.${orgId}`;
}

export function readSelection(userId: string, orgId: string): AdvisorSelection | null {
  try {
    const raw = sessionStorage.getItem(selectionStorageKey(userId, orgId));
    if (!raw) return null;
    const value = JSON.parse(raw);
    if (typeof value?.repositoryId !== 'string' || !value.repositoryId) return null;
    return { repositoryId: value.repositoryId, conversationId: typeof value.conversationId === 'string' ? value.conversationId : null };
  } catch {
    return null;
  }
}

export function writeSelection(userId: string, orgId: string, selection: AdvisorSelection | null): void {
  try {
    const key = selectionStorageKey(userId, orgId);
    if (selection) sessionStorage.setItem(key, JSON.stringify(selection));
    else sessionStorage.removeItem(key);
  } catch {
    /* storage unavailable */
  }
}

/**
 * Forgets every remembered Advisor selection. Called on organization switch
 * and sign-out so another organization's conversation IDs never outlive
 * access to them in this browser.
 */
export function clearAdvisorStorage(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(STORAGE_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    /* storage unavailable */
  }
}

export function newClientRequestId(): string {
  return crypto.randomUUID();
}
